import { Mesh, Program, Renderer, Triangle, Vec3 } from 'ogl';
import { useEffect, useRef } from 'react';
import { AiOrbProps, AiOrbState, StripeAnimation } from './AiOrb.types';

// Helper to convert hex color to normalized RGB Vec3
const hexToVec3 = (hex: string): Vec3 => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return new Vec3(
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255
    );
  }
  return new Vec3(0.5, 0.5, 0.5);
};

// Internal state configuration — not exported, fully hardcoded
type InternalStateConfig = {
  // Animation
  animation: StripeAnimation;
  amplitude: number;
  speed: number;
  rotationSpeed: number;
  scale: number;
  // Color mode: 'solid' or 'shine'
  colorMode: 'solid' | 'shine';
  color: Vec3;
  // Shine params (only used when colorMode === 'shine')
  shineBaseColor: Vec3;
  shineHighlightColor: Vec3;
  shineSpeed: number;
  shineWidth: number;
  shineFrequency: number;
};

// Hardcoded state configs from final orb config
const STATE_CONFIGS: Record<AiOrbState, InternalStateConfig> = {
  idle: {
    animation: 'none',
    amplitude: 0.07,
    speed: 1.0,
    rotationSpeed: 0.1,
    scale: 0.55,
    colorMode: 'solid',
    color: hexToVec3('#ADBFD9'),
    shineBaseColor: hexToVec3('#ADBFD9'),
    shineHighlightColor: hexToVec3('#ADBFD9'),
    shineSpeed: 0.5,
    shineWidth: 0.5,
    shineFrequency: 1.5,
  },
  listening: {
    animation: 'none',
    amplitude: 0.07,
    speed: 0.8,
    rotationSpeed: 0.0,
    scale: 0.6,
    colorMode: 'shine',
    color: hexToVec3('#FFDA54'),
    shineBaseColor: hexToVec3('#FFDA54'),
    shineHighlightColor: hexToVec3('#FFE5B4'),
    shineSpeed: 0.5,
    shineWidth: 0.5,
    shineFrequency: 1.5,
  },
  speaking: {
    animation: 'none',
    amplitude: 0.07,
    speed: 1.0,
    rotationSpeed: 0.2,
    scale: 0.6,
    colorMode: 'shine',
    color: hexToVec3('#3388FF'),
    shineBaseColor: hexToVec3('#3388FF'),
    shineHighlightColor: hexToVec3('#8B5ED4'),
    shineSpeed: 0.5,
    shineWidth: 0.5,
    shineFrequency: 1.5,
  },
  thinking: {
    animation: 'sequential',
    amplitude: 0.14,
    speed: 2.2,
    rotationSpeed: 0.25,
    scale: 0.55,
    colorMode: 'shine',
    color: hexToVec3('#E3D8F4'),
    shineBaseColor: hexToVec3('#E3D8F4'),
    shineHighlightColor: hexToVec3('#E5F0FF'),
    shineSpeed: 0.5,
    shineWidth: 0.5,
    shineFrequency: 1.5,
  },
  waiting: {
    animation: 'breathing',
    amplitude: 0.09,
    speed: 0.9,
    rotationSpeed: 0.1,
    scale: 0.5,
    colorMode: 'shine',
    color: hexToVec3('#CCE1FF'),
    shineBaseColor: hexToVec3('#CCE1FF'),
    shineHighlightColor: hexToVec3('#E5F0FF'),
    shineSpeed: 0.5,
    shineWidth: 0.5,
    shineFrequency: 1.5,
  },
};

// Animation type mapping for shader
const ANIMATION_MAP: Record<StripeAnimation, number> = {
  none: -1,
  sequential: 1,
  breathing: 2,
};

const DEFAULT_AUDIO_LEVELS = [0, 0, 0, 0, 0, 0, 0];

export function AiOrb({ state, audioLevels }: AiOrbProps) {
  return <Orb state={state} audioLevels={audioLevels} />;
}

export default function Orb({
  state = 'idle',
  audioLevels = DEFAULT_AUDIO_LEVELS,
}: {
  state?: string;
  audioLevels?: number[];
}) {
  const ctnDom = useRef<HTMLDivElement>(null);
  const stateRef = useRef(state);
  const audioLevelsRef = useRef(audioLevels);

  // Keep refs in sync with props
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    audioLevelsRef.current = audioLevels;
  }, [audioLevels]);

  const vert = /* glsl */ `
    precision highp float;
    attribute vec2 position;
    attribute vec2 uv;
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  const frag = /* glsl */ `
    precision highp float;

    uniform float iTime;
    uniform vec3 iResolution;
    uniform vec3 uColor;
    uniform float uRot;
    uniform float uScale;
    uniform float uAudioLevels[7];
    uniform float uThinkingAmount;
    uniform float uThinkingAnimationType;
    uniform float uThinkingAmplitude;
    uniform float uThinkingSpeed;

    // Shine uniforms
    uniform float uGradientType;        // 0=solid, 4=shine
    uniform vec3 uShineBaseColor;
    uniform vec3 uShineHighlightColor;
    uniform float uShineSpeed;
    uniform float uShineWidth;
    uniform float uShineFrequency;

    varying vec2 vUv;

    // 2D rotation
    vec2 rotate(vec2 v, float a) {
      float s = sin(a);
      float c = cos(a);
      return vec2(c * v.x - s * v.y, s * v.x + c * v.y);
    }

    // Pseudo-random hash for shine
    float hash11(float p) {
      p = fract(p * 0.1031);
      p *= p + 33.33;
      p *= p + p;
      return fract(p);
    }

    // Get audio level for stripe index (0-6)
    float getAudioLevel(int index) {
      if (index == 0) return uAudioLevels[5];
      if (index == 1) return uAudioLevels[3];
      if (index == 2) return uAudioLevels[1];
      if (index == 3) return uAudioLevels[0];
      if (index == 4) return uAudioLevels[2];
      if (index == 5) return uAudioLevels[4];
      if (index == 6) return uAudioLevels[6];
      return 0.0;
    }

    // Thinking animation: Sequential - stripes animate one by one (back and forth)
    float getSequentialAnimation(int stripeIndex, float time, float amplitude, float speed) {
      float cycle = mod(time * 1.5 * speed, 12.0);
      float cyclePos;
      if (cycle < 6.0) {
        cyclePos = cycle;
      } else {
        cyclePos = 12.0 - cycle;
      }
      float stripeF = float(stripeIndex);
      float dist = abs(cyclePos - stripeF);
      float activation = max(0.0, 1.0 - dist * 0.7);
      return activation * amplitude;
    }

    // Thinking animation: Breathing - all stripes pulse uniformly
    float getBreathingAnimation(int stripeIndex, float time, float amplitude, float speed) {
      float breath = (sin(time * 2.0 * speed) * 0.5 + 0.5);
      return breath * amplitude;
    }

    // Get thinking animation value based on type
    float getThinkingAnimation(int stripeIndex, float time, float animationType, float amplitude, float speed) {
      if (animationType < 0.0) return 0.0; // none
      if (animationType < 1.5 && animationType >= 0.5) return getSequentialAnimation(stripeIndex, time, amplitude, speed);
      if (animationType < 2.5 && animationType >= 1.5) return getBreathingAnimation(stripeIndex, time, amplitude, speed);
      return 0.0;
    }

    void main() {
      // Normalize UV with extra headroom for audio extension
      vec2 center = iResolution.xy * 0.5;
      float size = min(iResolution.x, iResolution.y);
      vec2 uv = (gl_FragCoord.xy - center) / size * 2.6;

      float radius = length(uv);
      float angle = atan(uv.y, uv.x);

      // Apply slow rotation, then rotate by 45 degrees for diagonal stripes
      vec2 rotUV = rotate(uv, uRot);
      rotUV = rotate(rotUV, radians(45.0));

      // Stripe parameters - scaled with uScale so stripes stay proportional to orb size
      float stripeWidth = 0.21 * uScale;
      float gapWidth = 0.21 * uScale;
      float period = stripeWidth + gapWidth;
      float numStripes = 7.0;

      // Center the pattern on the middle stripe
      float patternStart = -3.0 * period - stripeWidth * 0.5;

      // Position within the stripe pattern
      float pos = rotUV.x;
      float localPos = pos - patternStart;
      float totalWidth = numStripes * period;

      // Calculate stripe index (0-6)
      int stripeIndex = int(floor(localPos / period));
      float withinStripe = mod(localPos, period);
      bool inBounds = localPos >= 0.0 && localPos < totalWidth;
      bool onStripe = withinStripe < stripeWidth && inBounds;

      // Soft stripe edges for anti-aliasing (prevents gray fringe on rotated stripes)
      float edgeSmooth = 0.005;
      float stripeMask = smoothstep(0.0, edgeSmooth, withinStripe)
                       * (1.0 - smoothstep(stripeWidth - edgeSmooth, stripeWidth, withinStripe));
      float boundsMask = smoothstep(0.0, edgeSmooth, localPos)
                       * (1.0 - smoothstep(totalWidth - edgeSmooth, totalWidth, localPos));
      stripeMask *= boundsMask;

      // Get audio level for current stripe and calculate extension
      float audioLevel = 0.0;
      float audioExtension = 0.0;
      float thinkingExtension = 0.0;

      if (onStripe && stripeIndex >= 0 && stripeIndex < 7) {
        audioLevel = getAudioLevel(stripeIndex);
        audioExtension = audioLevel * 0.52 * uScale;
        thinkingExtension = getThinkingAnimation(stripeIndex, iTime, uThinkingAnimationType, uThinkingAmplitude, uThinkingSpeed) * uScale;
      }

      // Blend between audio extension and thinking extension
      float finalExtension = mix(audioExtension, thinkingExtension, uThinkingAmount);

      // Circular mask (scaled for UV headroom)
      float baseRadius = 1.235 * uScale;
      float orbRadius = baseRadius + finalExtension;
      float circleMask = 1.0 - smoothstep(orbRadius - 0.003, orbRadius + 0.003, radius);

      // Default: transparent
      vec3 color = vec3(0.0);
      float alpha = 0.0;

      if (onStripe) {
        alpha = circleMask * stripeMask;

        if (uGradientType >= 3.5 && uGradientType < 4.5) {
          // Shine effect — highlight sweeps across from random angles with idle gaps
          float sSpeed = max(uShineSpeed, 0.1);
          float sFreq = max(uShineFrequency, 0.1);

          float baseShineDur = 1.8;
          float basePause = 2.5;

          // Find current cycle. Use average cycle duration to estimate the index,
          // then compute exact cumulative time from a nearby starting point.
          // Each cycle varies by ±15%, so the estimate is off by at most a few cycles.
          float avgCycleDur = (baseShineDur * 1.0) / sSpeed + (basePause * 1.0) / sFreq;
          float elapsed = iTime;
          float roughIndex = floor(elapsed / avgCycleDur);

          // Try a few starting points, walking back to ensure we don't overshoot
          float sCycleIndex = max(roughIndex - 3.0, 0.0);
          float sCycleStart = 0.0;
          // Compute cumulative start for our starting index using the average
          sCycleStart = sCycleIndex * avgCycleDur;
          // Now walk forward with exact per-cycle durations to find the right cycle
          for (int i = 0; i < 32; i++) {
            float sDur = baseShineDur * (0.85 + hash11(sCycleIndex * 4.37) * 0.3) / sSpeed;
            float gDur = basePause * (0.7 + hash11(sCycleIndex * 6.91) * 0.6) / sFreq;
            if (sCycleStart + sDur + gDur > elapsed) break;
            sCycleStart += sDur + gDur;
            sCycleIndex += 1.0;
          }

          float shineDur = baseShineDur * (0.85 + hash11(sCycleIndex * 4.37) * 0.3) / sSpeed;
          float timeInShine = elapsed - sCycleStart;
          bool inShine = timeInShine < shineDur;

          float highlight = 0.0;
          if (inShine) {
            // Random angle per cycle
            float randAngle = hash11(sCycleIndex * 2.53) * 6.2832;
            vec2 shineUV = rotate(rotUV, randAngle);
            float t = shineUV.y / (uScale * 2.5) + 0.5;

            // Progress 0→1 over the shine duration
            float progress = timeInShine / shineDur;
            progress = progress * progress * (3.0 - 2.0 * progress);

            // Width-dependent overshoot for smooth entry/exit
            float width = uShineWidth * 0.8 + 0.1;
            float overshoot = 0.3 + width * 2.5;
            float shinePos = mix(-overshoot, 1.0 + overshoot, progress);

            float dist = abs(t - shinePos);

            // Smooth Gaussian falloff
            float sigma = width * 1.8;
            highlight = exp(-dist * dist / (2.0 * sigma * sigma));
          }

          color = mix(uShineBaseColor, uShineHighlightColor, highlight);
        } else {
          // Solid color
          color = uColor;
        }
      }

      gl_FragColor = vec4(color, alpha);
    }
  `;

  useEffect(() => {
    const container = ctnDom.current;
    if (!container) return;

    const renderer = new Renderer({ alpha: true, premultipliedAlpha: false });
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    (container as HTMLElement).appendChild(gl.canvas);

    const geometry = new Triangle(gl);

    const initialConfig =
      STATE_CONFIGS[state as AiOrbState] || STATE_CONFIGS.idle;

    const program = new Program(gl, {
      vertex: vert,
      fragment: frag,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Vec3(0, 0, 0) },
        uColor: {
          value: new Vec3(
            initialConfig.color.x,
            initialConfig.color.y,
            initialConfig.color.z
          ),
        },
        uRot: { value: 0 },
        uScale: { value: initialConfig.scale },
        uAudioLevels: { value: new Float32Array([0, 0, 0, 0, 0, 0, 0]) },
        uThinkingAmount: { value: 0 },
        uThinkingAnimationType: { value: -1 },
        uThinkingAmplitude: { value: 0 },
        uThinkingSpeed: { value: 1.0 },
        uGradientType: { value: initialConfig.colorMode === 'shine' ? 4 : 0 },
        uShineBaseColor: {
          value: new Vec3(
            initialConfig.shineBaseColor.x,
            initialConfig.shineBaseColor.y,
            initialConfig.shineBaseColor.z
          ),
        },
        uShineHighlightColor: {
          value: new Vec3(
            initialConfig.shineHighlightColor.x,
            initialConfig.shineHighlightColor.y,
            initialConfig.shineHighlightColor.z
          ),
        },
        uShineSpeed: { value: initialConfig.shineSpeed },
        uShineWidth: { value: initialConfig.shineWidth },
        uShineFrequency: { value: initialConfig.shineFrequency },
      },
      transparent: true,
    });

    const mesh = new Mesh(gl, { geometry, program });

    function resize() {
      if (!container) return;
      const dpr = window.devicePixelRatio || 1;
      const width = (container as HTMLElement).clientWidth;
      const height = (container as HTMLElement).clientHeight;
      renderer.setSize(width * dpr, height * dpr);
      gl.canvas.style.width = width + 'px';
      gl.canvas.style.height = height + 'px';
      program.uniforms.iResolution.value.set(
        gl.canvas.width,
        gl.canvas.height,
        gl.canvas.width / gl.canvas.height
      );
    }
    window.addEventListener('resize', resize);
    resize();

    let rafId: number;
    let lastTime = performance.now();
    let rotation = 0;
    let currentScale = initialConfig.scale;
    let currentRotSpeed = initialConfig.rotationSpeed;
    let currentThinkingAmount = 0;
    const currentColor = new Vec3(
      initialConfig.color.x,
      initialConfig.color.y,
      initialConfig.color.z
    );
    const currentShineBase = new Vec3(
      initialConfig.shineBaseColor.x,
      initialConfig.shineBaseColor.y,
      initialConfig.shineBaseColor.z
    );
    const currentShineHighlight = new Vec3(
      initialConfig.shineHighlightColor.x,
      initialConfig.shineHighlightColor.y,
      initialConfig.shineHighlightColor.z
    );
    const currentAudioLevels = [0, 0, 0, 0, 0, 0, 0];

    const update = (t: number) => {
      rafId = requestAnimationFrame(update);

      const dt = (t - lastTime) * 0.001;
      lastTime = t;

      program.uniforms.iTime.value = t * 0.001;

      // Get config for current state
      const config =
        STATE_CONFIGS[stateRef.current as AiOrbState] || STATE_CONFIGS.idle;
      const isAudioActive =
        stateRef.current === 'listening' || stateRef.current === 'speaking';

      // Smooth transitions
      currentRotSpeed += (config.rotationSpeed - currentRotSpeed) * 0.2;
      currentScale += (config.scale - currentScale) * 0.25;

      rotation += dt * currentRotSpeed;
      program.uniforms.uRot.value = rotation;
      program.uniforms.uScale.value = currentScale;

      // Smooth color transition
      currentColor.lerp(config.color, 0.25);
      program.uniforms.uColor.value.copy(currentColor);

      // Gradient type
      program.uniforms.uGradientType.value =
        config.colorMode === 'shine' ? 4 : 0;

      // Smooth shine color transitions (lerp instead of copy for smooth state changes)
      currentShineBase.lerp(config.shineBaseColor, 0.25);
      currentShineHighlight.lerp(config.shineHighlightColor, 0.25);
      program.uniforms.uShineBaseColor.value.copy(currentShineBase);
      program.uniforms.uShineHighlightColor.value.copy(currentShineHighlight);
      program.uniforms.uShineSpeed.value = config.shineSpeed;
      program.uniforms.uShineWidth.value = config.shineWidth;
      program.uniforms.uShineFrequency.value = config.shineFrequency;

      // Animate stripe animation amount
      const hasStripeAnimation = !isAudioActive && config.animation !== 'none';
      const targetAnimationAmount = hasStripeAnimation ? 1.0 : 0.0;
      currentThinkingAmount +=
        (targetAnimationAmount - currentThinkingAmount) * 0.1;
      program.uniforms.uThinkingAmount.value = currentThinkingAmount;

      // Update animation type, amplitude, and speed
      program.uniforms.uThinkingAnimationType.value =
        ANIMATION_MAP[config.animation] ?? -1;
      program.uniforms.uThinkingAmplitude.value = config.amplitude;
      program.uniforms.uThinkingSpeed.value = config.speed;

      // Update audio levels with smoothing
      const audioAmplitude = stateRef.current === 'listening' ? 0.6 : 0.8;
      const targetAudioLevels = isAudioActive
        ? audioLevelsRef.current
        : DEFAULT_AUDIO_LEVELS;
      for (let i = 0; i < 7; i++) {
        const target = Math.max(
          (targetAudioLevels[i] || 0) * audioAmplitude,
          0.2
        );
        const smoothing = currentAudioLevels[i] < target ? 0.3 : 0.1;
        currentAudioLevels[i] += (target - currentAudioLevels[i]) * smoothing;
      }
      program.uniforms.uAudioLevels.value = currentAudioLevels;

      renderer.render({ scene: mesh });
    };

    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      if (container && gl.canvas.parentNode === container) {
        container.removeChild(gl.canvas);
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return (
    <div
      ref={ctnDom}
      className="orb-container"
      style={{ width: '100%', height: '100%' }}
    />
  );
}
